import { FC, useCallback, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';

import { Input } from '@dynamika-shared/ui/Input/Input';
import { textFormatter } from 'src/utils/textFormatter';
import { DropdownChipsTag } from './DropdownChipsTag';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { CheckStrokeIcon } from '@dynamika-shared/ui/icons';

import { ChipsFilterProps } from './ChipsFilter';
import { createEntries } from 'src/utils/objectHelpers';

import {
  AuditAllFilterItem,
  PossibleFilters,
} from '@restore/auditTrailsFilters/auditFilters.types';

export type SubChipsProps = Omit<ChipsFilterProps, 'showSubFilters'>;

type SubFilter = {
  [key: string]: string;
};

type SelectedFilters = {
  [key: string]: SubFilter;
};

export const SubChips: FC<SubChipsProps> = ({
  subFilters,
  allFilters,
  actionBySearch,
  selectedFilters,
  search,
  eventSearch,
  closeTag,
  setSearch,
  setEventSearch,
  filtersFetch,
  setActionBySearch,
  toggleSelectedFilter,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [closeOnKeyDown, setCloseOnKeyDown] = useState<boolean>(false);

  const selectedFiltersMap = useMemo(
    () =>
      createEntries(selectedFilters).reduce((prev, curr) => {
        const [selectedFilterKey, selectedFilterValue] = curr;
        if ((selectedFilterValue as string[]).length) {
          const subFilters: SubFilter = {};
          for (const key of selectedFilterValue as string[]) {
            subFilters[key] = textFormatter(key.split('_').join(' '));
          }
          return { ...prev, [selectedFilterKey]: subFilters };
        } else {
          return { ...prev, [selectedFilterKey]: {} };
        }
      }, {} as SelectedFilters),
    [selectedFilters]
  );

  const filteredValues = useMemo(() => {
    const lowerCaseSearchValue = searchValue.toLowerCase();
    const filtered = allFilters[searchKey as keyof typeof allFilters]?.filter(
      item => item.label.toLowerCase().includes(lowerCaseSearchValue)
    );
    const result = { ...allFilters, [searchKey]: filtered };
    return searchValue ? result : allFilters;
  }, [searchValue, searchKey, allFilters]);

  const sortOption = (a: AuditAllFilterItem, b: AuditAllFilterItem) => {
    const labelA = a?.label?.toUpperCase();
    const labelB = b?.label?.toUpperCase();
    if (labelA < labelB) return -1;
    if (labelA > labelB) return 1;
    return 0;
  };

  const handleChangeSearchInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string
  ) => {
    const searchVal = e.target.value;
    setSearchValue(searchVal);
    setSearchKey(key);
    if (key === 'actionBy') {
      setActionBySearch(searchVal);
    } else if (key === 'event') {
      setEventSearch(searchVal);
    } else {
      setSearch(searchVal);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    key: string
  ) => {
    if (e.key === 'Enter') {
      const value = (e.target as HTMLInputElement)?.value?.trim();
      if (key === 'actionBy') {
        filtersFetch({ actionBy: value });
      } else if (key === 'event') {
        filtersFetch({ event: value });
      } else {
        filtersFetch({ patientCode: value });
      }
      setCloseOnKeyDown(true);
    }
  };

  const children = useCallback(
    (
      array: AuditAllFilterItem[],
      selectedFiltersMap: { [key: string]: string }
    ) => {
      const sortedFilters = array.sort(sortOption);
      return sortedFilters.map((item: AuditAllFilterItem) => {
        return {
          key: item.key,
          label: (
            <div className='flex items-center justify-between gap-2'>
              <CustomTooltip label={item.label} MAX_LABEL_LENGTH={50} />
              {selectedFiltersMap?.[item.key] && (
                <CheckStrokeIcon width={24} height={24} />
              )}
            </div>
          ),
        };
      });
    },
    []
  );

  // NOTE: each free-text filter keeps its typed value in dedicated state;
  // map them by key so the input value and chip label share one source
  const searchValueByKey: Partial<Record<PossibleFilters, string>> = {
    actionBy: actionBySearch,
    event: eventSearch,
    subjectId: search,
  };

  return (
    <div className='mt-[1rem] flex items-center flex-wrap gap-[1.2rem]'>
      {subFilters.map(([key, filter]) => {
        const isSubjectId = key === 'subjectId';
        const isActionBy = key === 'actionBy';
        const isEvent = key === 'event';
        const searchableFilters = isSubjectId || isActionBy || isEvent;
        const isFreeTextLabel = isActionBy || isEvent;

        const selectedLabels = Object.values(selectedFiltersMap[key]);

        const selectedLabel =
          selectedLabels.length > 1
            ? `${selectedLabels[0]} +${selectedLabels.length - 1}`
            : selectedLabels[0];

        const dynamicLabel = isFreeTextLabel
          ? searchValueByKey[key] || filter.label
          : selectedLabel;

        const filterLabel =
          isEmpty(selectedFiltersMap[key]) && !isFreeTextLabel
            ? filter.label
            : dynamicLabel;

        const searchInFilters = [
          {
            key: '1',
            type: 'group' as const,
            label: (
              <div>
                <div className='mb-[8px] text-spanishGray'>Filter Value</div>
                <Input
                  value={searchValueByKey[key] ?? ''}
                  onChange={e => handleChangeSearchInput(e, key)}
                  className='!mt-0 h-[4.8rem] !rounded-[3.2rem] px-2'
                  placeholder='Type in value'
                  onKeyDown={e => handleKeyDown(e, key)}
                  maxLength={45}
                />
              </div>
            ),
          },
        ];

        const dropdownFilters = [
          {
            key: '2',
            type: 'group' as const,
            label: filter.label,
            children: children(
              [...(filteredValues[key] ?? [])],
              selectedFiltersMap[key]
            ),
            className: 'max-h-[20rem] h-full overflow-auto',
          },
        ];

        const items = searchableFilters ? searchInFilters : dropdownFilters;

        return (
          <DropdownChipsTag
            key={key}
            keyFilter={key}
            onClick={item =>
              toggleSelectedFilter({ name: key, value: item.key })
            }
            // NOTE: actionBy, subjectId and event use free-text input;
            // other filters use the dropdown list
            items={items}
            name={filterLabel}
            active={filter.active}
            closeTag={closeTag}
            closeOnKeyDown={closeOnKeyDown}
            handleKeyDown={setCloseOnKeyDown}
            menuClassName={
              key === 'event' || key === 'businessProcess'
                ? 'w-[34rem]'
                : undefined
            }
          />
        );
      })}
    </div>
  );
};
